/*
 * Copyright (C) 2020-2024 The FrontDOOR Corp, Inc. All Rights Reserved.
 *
 * This software is the confidential and proprietary information of The FrontDOOR Corp, Inc.
 * ("Confidential Information").  You shall not disclose such Confidential Information and
 * shall use it only in accordance with the terms of the license agreement you entered into
 * with The FrontDOOR Corp, Inc.
 */

/**
 * Add page modal to be rendered on click of Add New store button inside store page of dedicated delivery.
 *
 * @version 1.0 26th Feb 2024
 * @author gbiswajeeta
 */

import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Grid,
  MenuItem,
} from "@mui/material";
import { Form, Formik } from "formik";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import * as Yup from "yup";
import getAxiosError from "../../../../app/api/axiosError";
import { persistNewStore } from "../../../../app/features/dedicatedDelivery/storeSlice";
import { updateUserTokenToInvalid } from "../../../../app/features/login/loginSlice";
import {
  RootState,
  useAppDispatch,
} from "../../../../app/features/store/store";
import { AddStoreRequest } from "../../../../app/models/requestModels/StoreRequest";
import FormikTextField from "../../../../components/common/formikFields/FormikTextField";
import Loader from "../../../../components/common/loader/Loader";
import SubmitModalWithFormik from "../../../../components/common/modalPopup/SubmitModalWithFormik";
import { STATES_LIST } from "../../../fdp/FdpConstants";
import {
  ADDRESS_REGEX,
  ADDRESS_REGEX_INVALID_MSG,
  AIRPORT_CODE_REGEX,
  AIRPORT_CODE_REGEX_INVALID_MSG,
  CITY_REGEX,
  CITY_REGEX_INVALID_MSG,
  CLIENT_STORE_ID_REGEX,
  CLIENT_STORE_ID_REGEX_INVALID_MSG,
  ZIPCODE_REGEX,
  ZIPCODE_REGEX_INVALID_MSG,
} from "../../../../helpers/AppConstants";
import { useState } from "react";

// type to recieve parameter for this component to initialize
interface AddStoreModalProps {
  clientIdArg: number;
  clientNameArg: string;
  closeAddStoreModal: CallableFunction;
  onCompleted: CallableFunction;
}

// helper function to add data for add new store addition
function AddStoreFormModel(
  airportCode: string,
  clientStoreId: string,
  minimumPackageCount: number,

  address: string,
  city: string,
  state: string,
  zipcode: string,

  addressOne: string,
  cityOne: string,
  stateOne: string,
  zipcodeOne: string,

  addressTwo: string,
  cityTwo: string,
  stateTwo: string,
  zipcodeTwo: string,

  fdps: number[],
  clientId: number,
  storeId: string,
 
  createdBy: string,
  updatedBy: string
): AddStoreRequest {
  return {
    clientId: clientId,
    airportCode: airportCode,
    clientStoreId: clientStoreId,
    minimumPackagecount: minimumPackageCount,

    address: address,
    city: city,
    state: state,
    zipcode: zipcode,

    addressOne: addressOne,
    cityOne: cityOne,
    stateOne: stateOne,
    zipcodeOne: zipcodeOne,

    addressTwo: addressTwo,
    cityTwo: cityTwo,
    stateTwo: stateTwo,
    zipcodeTwo: zipcodeTwo,

    fdps: fdps.map((e) => ({ id: e })),

    storeId: storeId,
    createdBy: createdBy,
    updatedBy: updatedBy,
  };
}

// Add new store modal component defination
const AddStoreModal = ({
  clientIdArg,
  clientNameArg,
  closeAddStoreModal,
  onCompleted,
}: AddStoreModalProps) => {
  const initialValues: AddStoreRequest = {
    clientId: clientIdArg,
    airportCode: "",
    clientStoreId: "",
    minimumPackagecount:0,

    address: "",
    city: "",
    state: "",
    zipcode: "",

    addressOne: "",
    cityOne: "",
    stateOne: "",
    zipcodeOne: "",

    addressTwo: "",
    cityTwo: "",
    stateTwo: "",
    zipcodeTwo: "",

    fdps: [],
  };

  // data to keep in store
  const dispatch = useAppDispatch();
  const { isFdpLoading, fdpList } = useSelector(
    (state: RootState) => state.fdp
  );
  const { authResponse } = useSelector((state: RootState) => state.login);

  // import store data from "Redux-Store"
  const { storeData } = useSelector(
    (state: RootState) => state.dedicatedDeliveryStore
  );

  // validation rule while adding new store into the database
  const validationSchema = Yup.object().shape({
    clientId: Yup.number().required("Client Id is Required"),
    airportCode: Yup.string()
      .required("Nearest Airport Code is Required")
      .matches(AIRPORT_CODE_REGEX, AIRPORT_CODE_REGEX_INVALID_MSG),
    clientStoreId: Yup.string()
      .required("Client Store Id is Required")
      .matches(CLIENT_STORE_ID_REGEX, CLIENT_STORE_ID_REGEX_INVALID_MSG)
      .test(
        "checkClientStoreIdExistsWithClientAlready",
        "Client Store Id already exists for this client",
        /* async (value) => {
          const response =
            await agent.DedicatedDeliveryAgent.checkIfStoreAlreadyExists({
              clientId: clientIdArg,
              clientStoreId: value,
            });
          return !response.data;
        } */
        (value) => {
          const find = storeData.find((e) => e.clientStoreId === value);
          return find === undefined;
        }
      ),
    address: Yup.string()
      .required("Address is Required")
      .matches(ADDRESS_REGEX, ADDRESS_REGEX_INVALID_MSG),
    city: Yup.string()
      .required("City is Required")
      .matches(CITY_REGEX, CITY_REGEX_INVALID_MSG),
    state: Yup.string().required("State is Required"),
    zipcode: Yup.string()
      .required("Zipcode is Required")
      .matches(ZIPCODE_REGEX, ZIPCODE_REGEX_INVALID_MSG),
    addressOne: Yup.string().matches(ADDRESS_REGEX, ADDRESS_REGEX_INVALID_MSG),
    cityOne: Yup.string().matches(CITY_REGEX, CITY_REGEX_INVALID_MSG),
    stateOne: Yup.string(),
    zipcodeOne: Yup.string().matches(ZIPCODE_REGEX, ADDRESS_REGEX_INVALID_MSG),
    addressTwo: Yup.string().matches(ADDRESS_REGEX, ADDRESS_REGEX_INVALID_MSG),
    cityTwo: Yup.string().matches(CITY_REGEX, CITY_REGEX_INVALID_MSG),
    stateTwo: Yup.string(),
    zipcodeTwo: Yup.string().matches(ZIPCODE_REGEX, ZIPCODE_REGEX_INVALID_MSG),
    fdps: Yup.array().min(1, "Pick at least one fdp").of(Yup.number()),
  });

  //on form submit make server call to persist the store
  const handleSubmit = async (values: any) => {
    try {
      const addStoreDetails = AddStoreFormModel(
        values.airportCode,
        values.clientStoreId,
        values.minimumPackageCount,
       

        values.address,
        values.city,
        values.state,
        values.zipcode,
     
      

        values.addressOne,
        values.cityOne,
        values.stateOne,
        values.zipcodeOne,

        values.addressTwo,
        values.cityTwo,
        values.stateTwo,
        values.zipcodeTwo,
        

        values.fdps,
        values.clientId,
        values.airportCode.toUpperCase() +
          clientNameArg.substring(0, 1) +
          values.clientStoreId,
        authResponse?.userSettings?.userName ?? "",
        authResponse?.userSettings?.userName ?? ""
      );
      // console.log(addStoreDetails);
      const response = await dispatch(
        persistNewStore(addStoreDetails)
      ).unwrap();
      if (!response.isSuccess) {
        toast.error(response.message, {
          toastId: "fdpData",
        });
      }
      toast.success("Store has been added successfully.");
    } catch (error) {
      const appError = getAxiosError(error);
      if (appError.code === "400") {
        toast.error(
          "Store " +
            values.clientStoreId +
            " is already exists with client " +
            values.clientId,
          {
            toastId: "addStoreException",
          }
        );
      } else {
        toast.error(appError.errorMessage, {
          toastId: "addStoreException",
        });
      }
    } finally {
      onCompleted();
    }
    return true;
  };
  return (
    <>
      <Loader isLoading={isFdpLoading} />
      <Formik
        initialValues={{ ...initialValues }}
        validationSchema={validationSchema}
        onSubmit={(values: AddStoreRequest, { setSubmitting }) => {
          setSubmitting(true);
          handleSubmit(values);
          setSubmitting(false);
        }}
      >
        <SubmitModalWithFormik
          title={"Add New " + clientNameArg + " Store"}
          closeSubmitModal={closeAddStoreModal}
          childErrorMessage={""}
          allowSubmit={false}
          size="lg"
          isLoading={false}
          submitButtonName="Add"
          closeModalAfterSubmit={true}
        >
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Form>
              <Grid container rowSpacing={2} columnSpacing={2}>
                <Grid item xs={12} sm={12} md={12} lg={12}>
                  <FormikTextField
                    margin="normal"
                    id="clientId"
                    name="clientId"
                    label="Client Id"
                    size="small"
                    fullWidth
                    variant={"outlined"}
                    required
                    isDisabled={true}
                  />
                </Grid>
              </Grid>
              <Grid container rowSpacing={2} columnSpacing={2}>
                <Grid item xs={12} sm={6} md={6} lg={6}>
                  <FormikTextField
                    margin="normal"
                    id="airportCode"
                    name="airportCode"
                    label="Nearest Airport Code"
                    size="small"
                    fullWidth
                    variant={"outlined"}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={6} lg={6}>
                  <FormikTextField
                    margin="normal"
                    id="clientStoreId"
                    name="clientStoreId"
                    label="Client Store Id"
                    size="small"
                    fullWidth
                    variant={"outlined"}
                    required
                  />
                </Grid>
              </Grid>
              <Grid container rowSpacing={2} columnSpacing={2}>
                <Grid item xs={12} sm={6} md={6} lg={6}>
                  <FormikTextField
                    margin="normal"
                    id="address"
                    label="Address"
                    name="address"
                    size="small"
                    fullWidth
                    variant={"outlined"}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={6} lg={6}>
                  <FormikTextField
                    margin="normal"
                    id="city"
                    label="City"
                    name="city"
                    size="small"
                    fullWidth
                    variant={"outlined"}
                    required
                  />
                </Grid>
              </Grid>
              <Grid container rowSpacing={2} columnSpacing={2}>
                <Grid item xs={12} sm={6} md={6} lg={6}>
                  <FormikTextField
                    select
                    margin="normal"
                    id="state"
                    label="State"
                    name="state"
                    size="small"
                    fullWidth
                    variant={"outlined"}
                    SelectProps={{
                      MenuProps: { PaperProps: { style: { maxHeight: 150 } } },
                    }}
                    required
                  >
                    {STATES_LIST.map((state, index) => (
                      <MenuItem key={index} value={state}>
                        {state}
                      </MenuItem>
                    ))}
                  </FormikTextField>
                </Grid>
                <Grid item xs={12} sm={6} md={6} lg={6}>
                  <FormikTextField
                    margin="normal"
                    id="zipcode"
                    label="Zipcode"
                    name="zipcode"
                    size="small"
                    type="text"
                    fullWidth
                    variant={"outlined"}
                    required
                  />
                </Grid>
              </Grid>
              <Card>
                <CardHeader subheader="Alternate Address 1" />
                <CardContent>
                  <Grid container columnSpacing={2}>
                    <Grid item xs={6} sm={6} md={6} lg={3}>
                      <FormikTextField
                        margin="normal"
                        id="addressOne"
                        label="Address"
                        name="addressOne"
                        size="small"
                        fullWidth
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={6} sm={6} md={6} lg={3}>
                      <FormikTextField
                        margin="normal"
                        id="cityOne"
                        label="City"
                        name="cityOne"
                        size="small"
                        fullWidth
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={6} sm={6} md={6} lg={3}>
                      <FormikTextField
                        select
                        margin="normal"
                        id="stateOne"
                        label="State"
                        name="stateOne"
                        size="small"
                        fullWidth
                        variant="outlined"
                        SelectProps={{
                          MenuProps: {
                            PaperProps: { style: { maxHeight: 150 } },
                          },
                        }}
                      >
                        {STATES_LIST.map((state, index) => (
                          <MenuItem key={index} value={state}>
                            {state}
                          </MenuItem>
                        ))}
                      </FormikTextField>
                    </Grid>
                    <Grid item xs={6} sm={6} md={6} lg={3}>
                      <FormikTextField
                        margin="normal"
                        id="zipcodeOne"
                        label="Zipcode"
                        name="zipcodeOne"
                        size="small"
                        type="text"
                        fullWidth
                        variant="outlined"
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
              <Card style={{ marginTop: "10px" }}>
                <CardHeader subheader="Alternate Address 2" />
                <CardContent>
                  <Grid container columnSpacing={2}>
                    <Grid item xs={6} sm={6} md={6} lg={3}>
                      <FormikTextField
                        margin="normal"
                        id="addressTwo"
                        label="Address"
                        name="addressTwo"
                        size="small"
                        fullWidth
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={6} sm={6} md={6} lg={3}>
                      <FormikTextField
                        margin="normal"
                        id="cityTwo"
                        label="City"
                        name="cityTwo"
                        size="small"
                        fullWidth
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={6} sm={6} md={6} lg={3}>
                      <FormikTextField
                        select
                        margin="normal"
                        id="stateTwo"
                        label="State"
                        name="stateTwo"
                        size="small"
                        fullWidth
                        variant="outlined"
                        SelectProps={{
                          MenuProps: {
                            PaperProps: { style: { maxHeight: 150 } },
                          },
                        }}
                      >
                        {STATES_LIST.map((state, index) => (
                          <MenuItem key={index} value={state}>
                            {state}
                          </MenuItem>
                        ))}
                      </FormikTextField>
                    </Grid>
                    <Grid item xs={6} sm={6} md={6} lg={3}>
                      <FormikTextField
                        margin="normal"
                        id="zipcodeTwo"
                        label="Zipcode"
                        name="zipcodeTwo"
                        size="small"
                        type="text"
                        fullWidth
                        variant="outlined"
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
              <Grid item xs={12} sm={6}>
  <FormikTextField
    margin="normal"
    id="minimumPackageCount"
    label="Minimum Package Count"
    name="minimumPackageCount"
    size="small"
    type="number"
    fullWidth
    variant= "outlined" 
    InputProps={{
     
      inputProps: {
        min: 0,
        max: 99,
        maxLength: 2,
        onInput: (e:any) => {
          // Remove any non-digit characters
          e.target.value = e.target.value.replace(/[^\d]/g, '');
          // Limit to 2 digits
          if (e.target.value.length > 2) {
            e.target.value = e.target.value.slice(0, 2);
          }
          // Ensure value is between 0 and 99
          const num = parseInt(e.target.value);
          if (num > 99) {
            e.target.value = "99";
          }
        }
      }
    }}
    SelectProps={{
      MenuProps: {
        PaperProps: { style: { maxHeight: 150 } },
      },
    }}
  />
</Grid>
              <Grid container rowSpacing={2} columnSpacing={2}>
                <Grid item xs={12} sm={12} md={12} lg={12}>
                  <FormikTextField
                    select
                    margin="normal"
                    id="fdps"
                    label="Select Associated FDP(s)"
                    name="fdps"
                    size="small"
                    fullWidth
                    variant={"outlined"}
                    SelectProps={{
                      MenuProps: { PaperProps: { style: { maxHeight: 150 } } },
                      multiple: true,
                    }}
                    required
                  >
                    {fdpList.length > 0 ? fdpList
                      .filter((e) => e.active)
                      .sort((a, b) =>
                        a.companyName.localeCompare(b.companyName)
                      )
                      .map((fdp, index) => (
                        <MenuItem
                          key={index.toString() + fdp.id}
                          value={fdp.id}
                        >
                          {fdp.companyName}
                        </MenuItem>
                      )) : []}
                  </FormikTextField>
                  {/* <Select
                    required
                    placeholder="Select Associated Fdp(s)"
                    isMulti={true}
                    hideSelectedOptions={false}
                    closeMenuOnSelect={false}
                    isSearchable={true}
                    menuPlacement="auto"
                    {...field}
                    options={fdpList.filter((e) => e.active)}
                    onChange={(
                      newValue: MultiValue<FdpResponse>,
                      actionMeta: ActionMeta<FdpResponse>
                    ) => {
                      console.log(newValue, actionMeta);
                    }}
                  /> */}
                </Grid>
              </Grid>
            </Form>
          </Box>
        </SubmitModalWithFormik>
      </Formik>
    </>
  );
};

export default AddStoreModal;
